"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, TriangleAlert, BatteryWarning, CircleCheck, ChevronRight, Clock, Droplet, Settings, ShieldCheck, Wrench } from "lucide-react";
import { RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { ScrollArea } from "@/components/ui/scroll-area";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const upcomingTasks = [{
  id: "t1",
  title: "Spindle Bearing Inspection",
  due: "2 Days",
  status: "warning",
  machine: "Haas VF-2"
}, {
  id: "t2",
  title: "Coolant System Flush",
  due: "5 Days",
  status: "normal",
  machine: "Mazak Integrex"
}, {
  id: "t3",
  title: "Axis Motor Calibration",
  due: "1 Week",
  status: "normal",
  machine: "Okuma Genos"
}, {
  id: "t4",
  title: "Way Lube Refill",
  due: "2 Weeks",
  status: "normal",
  machine: "Doosan Puma"
}, {
  id: "t5",
  title: "Filter Replacement",
  due: "3 Weeks",
  status: "normal",
  machine: "Haas VF-2"
}];
const gaugeData = [{
  name: "Health",
  value: 92,
  fill: "var(--color-primary)"
}];
const chartConfig = {
  health: {
    label: "System Health",
    color: "var(--color-primary)"
  }
};
function AnimatedCard({
  children,
  direction,
  delayClass = "",
  className
}: {
  children: React.ReactNode;
  direction: "left" | "right" | "up";
  delayClass?: string;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const getTransform = () => {
    if (isVisible) return "translate-x-0 translate-y-0 opacity-100";
    switch (direction) {
      case "left":
        return "-translate-x-16 opacity-0";
      case "right":
        return "translate-x-16 opacity-0";
      case "up":
        return "translate-y-16 opacity-0";
      default:
        return "opacity-0";
    }
  };
  return <div ref={ref} className={cn("transition-all duration-1000 ease-out will-change-transform", getTransform(), delayClass, className)}>
      {children}
    </div>;
}
export default function MaintenanceCommandCenter() {
  return <section id="maintenance-command-center" className="relative w-full bg-background py-24 sm:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none flex justify-center items-center opacity-[0.03]">
        <div className="w-[800px] h-[800px] rounded-lg bg-primary blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 gap-12 items-center mb-16 max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col items-center text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">Preventative Maintenance Portal</h2>
            <p className="text-lg text-muted-foreground max-w-3xl">Track tasks, access procedures, back-up parameters, and keep your production running with our advanced maintenance portal. Many of our suggested tasks aren't even in machine manuals. More than 45 years of repair has shown us <i className="font-bold" data-ast-id="f:/src/pages/Home/sections/MaintenanceCommandCenter.tsx|sig:xoczmu" data-ast-start="4325" data-ast-end="4362">exactly </i>what causes breakdowns and downtime.&nbsp;</p>
          </div>
          <div className="relative group w-full flex flex-col items-center gap-8">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/10 to-secondary/10 blur-xl rounded-xl opacity-30 group-hover:opacity-50 transition duration-1000"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Main Machines List Dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/image_ma7bpkcu.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/image_ma7bpkcu.png-wvc-sizes" alt="Maintenance Command Center screenshot" className="relative rounded-lg border border-border shadow-2xl w-full max-w-6xl object-cover" loading="lazy" decoding="async" />
            
            <div className="flex flex-col items-center text-center mt-8 max-w-3xl">
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-4">Individual Machine Dashboards</h3>
              <p className="text-muted-foreground">Each machine gets a dashboard that populates with recommended maintenance tasks based on the machine type, keeping track of maintenance tasks for every machine that actually prevent breakdowns and downtime.</p>
            </div>

            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Machine Dashboard Screenshot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Machine Dashboard Screenshot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Machine Dashboard Screenshot.png-wvc-sizes" alt="Additional Preventative Maintenance Portal screenshot" className="relative rounded-lg border border-border shadow-2xl w-full max-w-6xl object-cover" loading="lazy" decoding="async" />
          </div>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 relative z-20">
            <AnimatedCard direction="left" delayClass="delay-100">
              
            </AnimatedCard>

            <AnimatedCard direction="up" delayClass="delay-300">
              
            </AnimatedCard>

            <AnimatedCard direction="right" delayClass="delay-500">
              
            </AnimatedCard>
          </div>
        </div>
      </div>
    </section>;
}